import { Slice } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';

import {
  BaseExtension,
  EditorOptions,
  EditorView,
  extensionType,
  transformListItemLikeElementsIntoLists,
} from '@lyearn/lygo-core';

import { b64toBlob, isLocalUrl } from '../../utils/upload';
import { convertHexToBase64, dispatchPasteEvent, extractImageDataFromRtf } from './utils';

export default class TransformPastedContent extends BaseExtension {
  get type(): string {
    return extensionType.toolbar;
  }

  get name(): string {
    return 'transform_pasted_content';
  }

  plugins(_editorOptions: EditorOptions) {
    return [
      new Plugin({
        props: {
          transformPastedHTML: (html: string) => {
            // Transforms Word specific list-like elements to the semantic HTML lists.
            // Lists in Word are represented by block elements with special attributes like:
            //
            // <p class=MsoListParagraphCxSpFirst style='mso-list:l1 level1 lfo1'>...</p>
            // Paragraph based list.
            // <h1 style='mso-list:l0 level1 lfo1'>...</h1>
            // Heading 1 based list.
            const htmlDoc = new DOMParser().parseFromString(html, 'text/html');
            transformListItemLikeElementsIntoLists(htmlDoc);
            return htmlDoc.body.innerHTML;
          },
          handlePaste: (view: EditorView, event: any, slice: Slice) => {
            // Replaces source attribute of all `<img>` elements representing regular
            // images with inlined base64 image representation extracted from RTF or
            // Blob data. This is required mainly from pasting of images from MS Word.
            const imagesHexSources = extractImageDataFromRtf(
              event.clipboardData.getData('text/rtf'),
            );
            const imageType = view.state.schema.nodes.image;
            let imageCount = 0;
            let imageNodes = [];
            for (const sliceContent of (slice.content as any).content) {
              if (sliceContent.type === imageType) {
                imageNodes.push(sliceContent);
              }
            }

            for (const imageNode of imageNodes) {
              if (imageNode.type === imageType && imageNode.attrs.src.startsWith('file://')) {
                if (
                  imageCount < imagesHexSources.length &&
                  imageNodes.length === imagesHexSources.length
                ) {
                  const newSrc = `data:${
                    imagesHexSources[imageCount].type
                  };base64,${convertHexToBase64(imagesHexSources[imageCount].hex)}`;
                  const newObjectURL = URL.createObjectURL(b64toBlob(newSrc));
                  imageNode.attrs.src = newObjectURL;
                  imageNode.attrs.alt = '';
                  imageCount++;
                } else {
                  imageNode.attrs.src = imageNode.attrs.alt = '';
                }
              }
            }

            if (imageCount) {
              dispatchPasteEvent(view, slice);
              return true;
            }

            // Convert long base64 url to smaller object url for to avoid crdt service
            // data overflow.
            if (imageNodes.length) {
              let isLocalImage = false;
              for (const imageNode of imageNodes) {
                if (isLocalUrl(imageNode.attrs.src)) {
                  const newObjectURL = URL.createObjectURL(b64toBlob(imageNode.attrs.src));
                  imageNode.attrs.src = newObjectURL;
                  imageNode.attrs.alt = '';
                  isLocalImage = true;
                }
              }

              if (isLocalImage) {
                dispatchPasteEvent(view, slice);
                return true;
              }
            }

            return false;
          },
        },
      }),
    ];
  }
}
